import React, { useContext, useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import UnderlinedTabs from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs';
import UnderlinedTab from '@wix/da-ds/pkg/Tabs/dsPresets/UnderlinedTabs/UnderlinedTab';
import { TabItem } from '@wix/da-ds/pkg/Tabs';
import StandardMenu from '@wix/da-ds/pkg/Dropdown/MenuPreset/StandardMenu';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import BetaBadge from '@wix/da-ds/pkg/BetaBadge';
import type { TabBarProps } from '../../types';
import { useScrollToActiveItem } from '../../hooks/useScrollToActiveItem';
import { useIsOverflowedHorizontal } from '../../hooks/useIsOverflowedHorizontal';
import { BiData, ContentNavbarClickBiEvent } from '@wix/da-bi/pkg/events';
import s from './TabBar.scss';

export type Props = Pick<
  TabBarProps,
  'items' | 'activeMenuItem' | 'className' | 'children'
>;

const DESKTOP_MAX_VISIBLE_ITEMS = 6;

function getMenuItems(
  items: NonNullable<Props['items']>,
  activeMenuItem: Props['activeMenuItem'],
  isMobile: boolean
): [NonNullable<Props['items']>, NonNullable<Props['items']>] {
  if (isMobile) {
    return [items, []];
  }

  // If an item that would normally be in the overflow menu is active,
  // un-overflow it and swap out the last non-overflowed item
  if (
    activeMenuItem &&
    items
      .slice(DESKTOP_MAX_VISIBLE_ITEMS)
      .map(item => item.subnav)
      .includes(activeMenuItem)
  ) {
    const visible = items
      .slice(0, DESKTOP_MAX_VISIBLE_ITEMS - 1)
      .concat(items.filter(item => item.subnav === activeMenuItem));
    return [visible, items.filter(item => !visible.includes(item))];
  } else {
    return [
      items.slice(0, DESKTOP_MAX_VISIBLE_ITEMS),
      items.slice(DESKTOP_MAX_VISIBLE_ITEMS),
    ];
  }
}

const TabBar: React.FC<Props> = ({
  items = [],
  activeMenuItem,
  className,
  children,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const { navListRef, activeItemRef } = useScrollToActiveItem(isMobile, 16);
  const { isOverflowed } = useIsOverflowedHorizontal(navListRef);

  const [actualItems, overflowItems] = useMemo((): [
    TabItem[],
    NullableDropdownItem[]
  ] => {
    const [visible, overflow] = getMenuItems(items, activeMenuItem, isMobile);
    return [
      visible.map(menuItem => ({
        index: menuItem.subnav,
        label: t(`browseHeader.page.${menuItem.subnav}`),
        labelExtras: menuItem.isBeta && (
          <BetaBadge
            badgeType="beta"
            variant="medium"
            className={s['beta-badge']}
          />
        ),
        href: menuItem.url,
        hasNotification: menuItem.hasNewContent,
        biData: BiData<ContentNavbarClickBiEvent>({
          evid: 120,
          link_name: menuItem.subnav,
          indicator: menuItem.hasNewContent ? 1 : 0,
        }),
      })),
      overflow.map(menuItem => ({
        link: menuItem.url,
        label: t(`browseHeader.page.${menuItem.subnav}`),
        biData: BiData<ContentNavbarClickBiEvent>({
          evid: 120,
          link_name: menuItem.subnav,
        }),
      })),
    ];
  }, [activeMenuItem, isMobile, items, t]);

  const setActiveItemRef = (node: HTMLLIElement) => {
    activeItemRef.current = node;
  };

  const tabSize = isMobile ? 'small' : 'big';

  return (
    <div
      className={classnames(
        s['root'],
        isOverflowed && s['scrollable'],
        className
      )}
      ref={navListRef}
    >
      {children}
      {!children && actualItems.length > 0 && (
        <div className={s['nav-list']}>
          <UnderlinedTabs
            className={s['tabs']}
            size={tabSize}
            items={actualItems}
            activeIndex={activeMenuItem}
            activeItemRef={setActiveItemRef}
            extraTabs={
              <>
                {overflowItems.length > 0 && (
                  <UnderlinedTab
                    className={s['more-menu']}
                    label={t('browseHeader.more')}
                    size={tabSize}
                    dropdown={
                      <StandardMenu
                        lightText
                        id="subnav-more"
                        className={s['more-list']}
                        items={overflowItems}
                      />
                    }
                  />
                )}
              </>
            }
          />
        </div>
      )}
    </div>
  );
};
TabBar.displayName = 'TabBar';

export default TabBar;
